import { workCategoryOptionsResponseSchema, type WorkCategoryOption } from '@houzy/schemas/partner-cockpit/onboarding';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRequest, retryQueryUnlessClientError } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';
import type { WorkCategory } from '../types';

export const workCategoryTranslationKey = (key: string): string => `WORK_CATEGORY__${key}`;

function translateLabel(t: TFunction, option: WorkCategoryOption): string {
    return t(workCategoryTranslationKey(option.key));
}

function toViewModel(t: TFunction, option: WorkCategoryOption): WorkCategory {
    return {
        uuid: option.uuid,
        label: translateLabel(t, option),
        subCategories: option.children.map((sub) => ({
            uuid: sub.uuid,
            label: translateLabel(t, sub),
            items: sub.children.map((leaf) => ({ uuid: leaf.uuid, label: translateLabel(t, leaf) })),
        })),
    };
}

export async function fetchWorkCategories(signal?: AbortSignal): Promise<WorkCategoryOption[]> {
    const response = await apiRequest(partnerCockpitApiPath('onboarding/work-categories/options'), {
        signal,
        responseSchema: workCategoryOptionsResponseSchema,
    });
    return response.categories;
}

export const workCategoriesQueryOptions = queryOptions({
    queryKey: ['onboarding', 'workCategories'],
    queryFn: ({ signal }) => fetchWorkCategories(signal),
    staleTime: STALE_TIME_MS,
    retry: retryQueryUnlessClientError,
});

export function useWorkCategories(): { data: WorkCategory[] } {
    const { t } = useTranslation();
    const { data } = useSuspenseQuery(workCategoriesQueryOptions);
    return useMemo(() => ({ data: data.map((option) => toViewModel(t, option)) }), [data, t]);
}
